import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { getPublicProposal, proposalEngagementUrl } from "../api/prospectingReportsApi";
import ProposalBlockRenderer from "../components/ProposalBlockRenderer";
import AcceptProposalPanel from "../components/AcceptProposalPanel";

/**
 * The vendor-facing proposal page. Served outside the auth shell (App.js) at
 * /proposal/:token. The token in the URL is the only credential; the page sends
 * no auth. Images load in the vendor's own browser.
 */
const ProposalPublicPage = () => {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | gone | notfound | error
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    // Keep the vendor's proposal out of search results.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  // Section engagement beacon: accumulate seconds each block is on screen and
  // flush once when the tab is hidden / the page is left (fetch keepalive so
  // the request survives unload). No personal data — block ids and seconds only.
  const secondsRef = useRef({});
  const typesRef = useRef({});
  const openedRef = useRef({});
  useEffect(() => {
    if (state !== "ok" || typeof IntersectionObserver === "undefined") return undefined;
    const nodes = Array.from(document.querySelectorAll("[data-block-id]"));
    const observer = new IntersectionObserver((entries) => {
      const now = Date.now();
      entries.forEach((entry) => {
        const id = entry.target.getAttribute("data-block-id");
        if (!id) return;
        typesRef.current[id] = entry.target.getAttribute("data-block-type") || "";
        if (entry.isIntersecting) {
          openedRef.current[id] = now;
        } else if (openedRef.current[id]) {
          secondsRef.current[id] = (secondsRef.current[id] || 0) + (now - openedRef.current[id]) / 1000;
          delete openedRef.current[id];
        }
      });
    }, { threshold: 0.4 });
    nodes.forEach((n) => observer.observe(n));

    let flushed = false;
    const flush = () => {
      if (flushed) return;
      const now = Date.now();
      Object.keys(openedRef.current).forEach((id) => {
        secondsRef.current[id] = (secondsRef.current[id] || 0) + (now - openedRef.current[id]) / 1000;
      });
      openedRef.current = {};
      const sections = Object.keys(secondsRef.current)
        .filter((id) => secondsRef.current[id] >= 1)
        .map((id) => ({ block_id: id, type: typesRef.current[id] || "", seconds: Math.round(secondsRef.current[id]) }));
      if (!sections.length) return;
      flushed = true;
      try {
        fetch(proposalEngagementUrl(token), {
          method: "POST",
          keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sections }),
        }).catch(() => {});
      } catch (_) { /* analytics must never break the vendor's page */ }
    };
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [state, token]);

  useEffect(() => {
    getPublicProposal(token)
      .then(({ data }) => { setProposal(data); setAccepted(data.status === "accepted"); setState("ok"); })
      .catch((e) => {
        const s = e?.response?.status;
        setState(s === 410 ? "gone" : s === 404 ? "notfound" : "error");
      });
  }, [token]);

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (state !== "ok") {
    const msg = state === "gone"
      ? "This proposal link has expired. Please contact your agent."
      : "This proposal could not be found. Please check the link or contact your agent.";
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#f4f7f8]">
        <div className="max-w-md text-center bg-white rounded-2xl border border-gray-100 p-8">
          <p className="text-gray-600">{msg}</p>
        </div>
      </div>
    );
  }

  const ctaBlock = (proposal.blocks || []).find((b) => b.type === "cta" && b.visible !== false);
  const acceptSlot = accepted ? (
    <div className="mt-4 rounded-lg bg-white/15 p-4 text-white">Thank you — you've accepted this proposal.</div>
  ) : (
    <AcceptProposalPanel
      token={token}
      block={ctaBlock ? ctaBlock.data : {}}
      requiredApprovals={proposal.required_approvals || 1}
      approvalsCount={proposal.approvals_count || 0}
      requiresEmail={!!proposal.requires_email}
      onAccepted={() => setAccepted(true)}
    />
  );

  const branding = proposal.branding || {};
  return (
    <div className="min-h-screen bg-white">
      <ProposalBlockRenderer
        blocks={proposal.blocks}
        theme={proposal.theme_colour || branding.primary_colour}
        branding={branding}
        agent={proposal.agent}
        comparables={proposal.comparables || []}
        acceptSlot={acceptSlot}
      />
      <p className="text-center text-xs text-gray-400 py-6 bg-[#f5f6f8]">
        {branding.footer_text || branding.agency_name || "Collings Real Estate"}
      </p>
    </div>
  );
};

export default ProposalPublicPage;
